/**
 * Notification DTO + helpers — notification-service `/api/notifications`.
 * Mirrors `net.nawam.notification.model.entity.Notification` (JsonView NotRead).
 *
 * Le centre de notifications combine REST (état initial + actions) et
 * WebSocket STOMP (temps réel). Cf. `fronts/WEBSOCKET_NOTIFICATIONS.md`.
 */

import { currentLang, translate } from '../services/locale.service';

/** `net.nawam.notification.model.enumeration.NotificationType` (12 valeurs). */
export type NotificationType =
  | 'RENTAL'
  | 'RENTAL_ACCEPTED'
  | 'RENTAL_DENIED'
  | 'RENTAL_STARTED'
  | 'RENTAL_ENDED'
  | 'RENTAL_CANCELED'
  | 'VEHICLE_STATUS_CHANGED'
  | 'VEHICLE_IN_REVIEW'
  | 'NEW_CHAT'
  // Chat : message rejeté car il contenait des coordonnées (l'expéditeur est averti).
  | 'WARNING'
  | 'INFO'
  | 'PROVISION_CONTRACT_SIGNING_REQUEST'
  | 'RENTAL_CONTRACT_SIGNING_REQUEST'
  // rental-service (suite)
  | 'RENTAL_REQUEST_EXPIRED'
  | 'RENTAL_REQUEST_REJECTED'
  | 'VEHICLE_REVIEWED_AVAILABLE'
  | 'VEHICLE_REVIEWED_REJECTED'
  | 'VEHICLE_DOCUMENT_EXPIRY'
  | 'PROVISION_WINDOW_UPDATED'
  | 'INVOICE_PAID'
  | 'KYC_SUBMITTED'
  // payment-service
  | 'PAYMENT_STATUS'
  // wallet-service
  | 'WALLET_TOPUP'
  | 'WALLET_CREDIT_DEBT'
  | 'DEBT_PAYMENT_REQUEST'
  | 'WITHDRAWAL_TO_VALIDATE'
  | 'WITHDRAWAL_DECIDED'
  | 'WITHDRAWAL_ADMIN_INFO'
  // operator-service
  | 'OPERATOR_APPROVED'
  | 'OPERATOR_CHANGE'
  | 'OPERATOR_CREDIT_LOW'
  | 'OPERATOR_CREDIT_EXHAUSTED'
  | 'OPERATOR_CREDIT_RESTORED'
  | 'LICENSE_ACTIVATED'
  | 'LICENSE_EXPIRED'
  | 'MODERATION_WARNING'
  | 'MODERATION_SUSPENSION'
  | 'PRESTATAIRE_DEBT_ALERT'
  | 'DEBT_REMINDER'
  | 'OPERATOR_DEBT_REMINDER'
  | 'KYC_REMINDER'
  | 'OPERATOR_KYC_SUBMITTED'
  | 'OPERATOR_KYC_APPROVED'
  | 'OPERATOR_KYC_REJECTED';

export interface Notification {
  id: number;
  userId?: number | null;
  type: NotificationType;
  messageFR?: string | null;
  messageEN?: string | null;
  /** Message dans la langue par défaut (= FR aujourd'hui). */
  message?: string | null;
  /** Id de l'entité liée (vehicle, rental…) — 0 si aucune. */
  contextId?: number | null;
  /** ⚠️ Chaîne JSON à parser ({@link parseNotificationData}) — porte le deeplink. */
  data?: string | null;
  read?: boolean;
  createdAt?: string | null;
}

/** Forme typée (best-effort) du champ `data` JSON. Tous les champs optionnels. */
export interface NotificationData {
  vehicleId?: number;
  rentalId?: number;
  invoiceId?: number;
  transactionId?: number;
  submissionLink?: string;
  /** KYC_SUBMITTED (§Customer) — id Customer (rental-service) à ouvrir. */
  customerId?: number;
  /** KYC_SUBMITTED — id user global du client. */
  customerUserId?: number;
  /** KYC_SUBMITTED — n° de permis soumis (aperçu). */
  licenseNumber?: string;
  [key: string]: unknown;
}

/** Parse le champ `data` (string JSON) de façon défensive. */
export function parseNotificationData(
  data: string | null | undefined,
): NotificationData | null {
  if (!data) return null;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? (parsed as NotificationData) : null;
  } catch {
    return null;
  }
}

/**
 * Message d'une notif **dans la langue active**.
 *
 * Le back stocke les deux versions (`messageFR` / `messageEN`) : on ne traduit
 * donc rien ici, on **choisit** le bon champ. Repli sur l'autre langue puis sur
 * `message` (langue par défaut back) avant de tomber sur le libellé du type.
 * Lit le signal `lang` → réactif au changement de langue depuis un template.
 */
export function notificationText(n: Notification): string {
  const [primary, secondary] =
    currentLang() === 'en' ? [n.messageEN, n.messageFR] : [n.messageFR, n.messageEN];
  return (
    primary ||
    n.message ||
    secondary ||
    translate(NOTIFICATION_TYPE_LABEL[n.type]) ||
    translate('notif.fallback')
  );
}

/** Valeurs = **clés i18n** (résolues au rendu via `| t`). */
export const NOTIFICATION_TYPE_LABEL: Record<NotificationType, string> = {
  RENTAL: 'notifType.RENTAL',
  RENTAL_ACCEPTED: 'notifType.RENTAL_ACCEPTED',
  RENTAL_DENIED: 'notifType.RENTAL_DENIED',
  RENTAL_STARTED: 'notifType.RENTAL_STARTED',
  RENTAL_ENDED: 'notifType.RENTAL_ENDED',
  RENTAL_CANCELED: 'notifType.RENTAL_CANCELED',
  VEHICLE_STATUS_CHANGED: 'notifType.VEHICLE_STATUS_CHANGED',
  VEHICLE_IN_REVIEW: 'notifType.VEHICLE_IN_REVIEW',
  NEW_CHAT: 'notifType.NEW_CHAT',
  WARNING: 'notifType.WARNING',
  INFO: 'notifType.INFO',
  PROVISION_CONTRACT_SIGNING_REQUEST: 'notifType.PROVISION_CONTRACT_SIGNING_REQUEST',
  RENTAL_CONTRACT_SIGNING_REQUEST: 'notifType.RENTAL_CONTRACT_SIGNING_REQUEST',
  OPERATOR_CHANGE: 'notifType.OPERATOR_CHANGE',
  RENTAL_REQUEST_EXPIRED: 'notifType.RENTAL_REQUEST_EXPIRED',
  RENTAL_REQUEST_REJECTED: 'notifType.RENTAL_REQUEST_REJECTED',
  VEHICLE_REVIEWED_AVAILABLE: 'notifType.VEHICLE_REVIEWED_AVAILABLE',
  VEHICLE_REVIEWED_REJECTED: 'notifType.VEHICLE_REVIEWED_REJECTED',
  VEHICLE_DOCUMENT_EXPIRY: 'notifType.VEHICLE_DOCUMENT_EXPIRY',
  PROVISION_WINDOW_UPDATED: 'notifType.PROVISION_WINDOW_UPDATED',
  INVOICE_PAID: 'notifType.INVOICE_PAID',
  KYC_SUBMITTED: 'notifType.KYC_SUBMITTED',
  PAYMENT_STATUS: 'notifType.PAYMENT_STATUS',
  WALLET_TOPUP: 'notifType.WALLET_TOPUP',
  WALLET_CREDIT_DEBT: 'notifType.WALLET_CREDIT_DEBT',
  DEBT_PAYMENT_REQUEST: 'notifType.DEBT_PAYMENT_REQUEST',
  WITHDRAWAL_TO_VALIDATE: 'notifType.WITHDRAWAL_TO_VALIDATE',
  WITHDRAWAL_DECIDED: 'notifType.WITHDRAWAL_DECIDED',
  WITHDRAWAL_ADMIN_INFO: 'notifType.WITHDRAWAL_ADMIN_INFO',
  OPERATOR_APPROVED: 'notifType.OPERATOR_APPROVED',
  OPERATOR_CREDIT_LOW: 'notifType.OPERATOR_CREDIT_LOW',
  OPERATOR_CREDIT_EXHAUSTED: 'notifType.OPERATOR_CREDIT_EXHAUSTED',
  OPERATOR_CREDIT_RESTORED: 'notifType.OPERATOR_CREDIT_RESTORED',
  LICENSE_ACTIVATED: 'notifType.LICENSE_ACTIVATED',
  LICENSE_EXPIRED: 'notifType.LICENSE_EXPIRED',
  MODERATION_WARNING: 'notifType.MODERATION_WARNING',
  MODERATION_SUSPENSION: 'notifType.MODERATION_SUSPENSION',
  PRESTATAIRE_DEBT_ALERT: 'notifType.PRESTATAIRE_DEBT_ALERT',
  DEBT_REMINDER: 'notifType.DEBT_REMINDER',
  OPERATOR_DEBT_REMINDER: 'notifType.OPERATOR_DEBT_REMINDER',
  KYC_REMINDER: 'notifType.KYC_REMINDER',
  OPERATOR_KYC_SUBMITTED: 'notifType.OPERATOR_KYC_SUBMITTED',
  OPERATOR_KYC_APPROVED: 'notifType.OPERATOR_KYC_APPROVED',
  OPERATOR_KYC_REJECTED: 'notifType.OPERATOR_KYC_REJECTED',
};

/** Métadonnée d'affichage : icône (clé) + palette Tailwind par type. */
export interface NotificationMeta {
  /** Clé d'icône simple consommée par le template (switch). */
  icon: 'rental' | 'vehicle' | 'chat' | 'contract' | 'info';
  palette: 'brand' | 'blue-light' | 'warning' | 'error' | 'gray';
}

export const NOTIFICATION_TYPE_META: Record<NotificationType, NotificationMeta> = {
  RENTAL: { icon: 'rental', palette: 'brand' },
  RENTAL_ACCEPTED: { icon: 'rental', palette: 'brand' },
  RENTAL_DENIED: { icon: 'rental', palette: 'error' },
  RENTAL_STARTED: { icon: 'rental', palette: 'blue-light' },
  RENTAL_ENDED: { icon: 'rental', palette: 'gray' },
  RENTAL_CANCELED: { icon: 'rental', palette: 'error' },
  VEHICLE_STATUS_CHANGED: { icon: 'vehicle', palette: 'blue-light' },
  VEHICLE_IN_REVIEW: { icon: 'vehicle', palette: 'warning' },
  NEW_CHAT: { icon: 'chat', palette: 'blue-light' },
  WARNING: { icon: 'info', palette: 'warning' },
  INFO: { icon: 'info', palette: 'gray' },
  PROVISION_CONTRACT_SIGNING_REQUEST: { icon: 'contract', palette: 'warning' },
  RENTAL_CONTRACT_SIGNING_REQUEST: { icon: 'contract', palette: 'warning' },
  OPERATOR_CHANGE: { icon: 'info', palette: 'brand' },
  RENTAL_REQUEST_EXPIRED: { icon: 'rental', palette: 'gray' },
  RENTAL_REQUEST_REJECTED: { icon: 'rental', palette: 'error' },
  VEHICLE_REVIEWED_AVAILABLE: { icon: 'vehicle', palette: 'brand' },
  VEHICLE_REVIEWED_REJECTED: { icon: 'vehicle', palette: 'error' },
  VEHICLE_DOCUMENT_EXPIRY: { icon: 'vehicle', palette: 'warning' },
  PROVISION_WINDOW_UPDATED: { icon: 'contract', palette: 'brand' },
  INVOICE_PAID: { icon: 'info', palette: 'brand' },
  KYC_SUBMITTED: { icon: 'info', palette: 'warning' },
  PAYMENT_STATUS: { icon: 'info', palette: 'blue-light' },
  WALLET_TOPUP: { icon: 'info', palette: 'brand' },
  WALLET_CREDIT_DEBT: { icon: 'info', palette: 'error' },
  DEBT_PAYMENT_REQUEST: { icon: 'info', palette: 'warning' },
  WITHDRAWAL_TO_VALIDATE: { icon: 'info', palette: 'warning' },
  WITHDRAWAL_DECIDED: { icon: 'info', palette: 'brand' },
  WITHDRAWAL_ADMIN_INFO: { icon: 'info', palette: 'blue-light' },
  OPERATOR_APPROVED: { icon: 'info', palette: 'brand' },
  OPERATOR_CREDIT_LOW: { icon: 'info', palette: 'warning' },
  OPERATOR_CREDIT_EXHAUSTED: { icon: 'info', palette: 'error' },
  OPERATOR_CREDIT_RESTORED: { icon: 'info', palette: 'brand' },
  LICENSE_ACTIVATED: { icon: 'info', palette: 'brand' },
  LICENSE_EXPIRED: { icon: 'info', palette: 'error' },
  MODERATION_WARNING: { icon: 'info', palette: 'warning' },
  MODERATION_SUSPENSION: { icon: 'info', palette: 'error' },
  PRESTATAIRE_DEBT_ALERT: { icon: 'info', palette: 'warning' },
  DEBT_REMINDER: { icon: 'info', palette: 'warning' },
  OPERATOR_DEBT_REMINDER: { icon: 'info', palette: 'warning' },
  KYC_REMINDER: { icon: 'info', palette: 'warning' },
  OPERATOR_KYC_SUBMITTED: { icon: 'info', palette: 'warning' },
  OPERATOR_KYC_APPROVED: { icon: 'info', palette: 'brand' },
  OPERATOR_KYC_REJECTED: { icon: 'info', palette: 'error' },
};

/**
 * Lookup **défensif** type→meta. Le back n'est pas contraint à un enum fermé
 * (cf. convention notifications) : un `type` non répertorié renverrait `undefined`
 * et un accès `.palette`/`.icon` ferait planter tout le centre de notifications.
 * Ce proxy garantit une meta valide (fallback `info`/`gray`) pour tout type.
 * Toujours consommer **celui-ci** dans les composants, jamais `NOTIFICATION_TYPE_META` brut.
 */
export const NOTIFICATION_TYPE_META_SAFE: Record<string, NotificationMeta> = new Proxy(
  NOTIFICATION_TYPE_META as Record<string, NotificationMeta>,
  { get: (target, prop: string) => target[prop] ?? { icon: 'info', palette: 'gray' } },
);

/**
 * Deeplink admin-web : route à ouvrir au clic sur une notif, dérivée du
 * `type` + des ids présents dans `data` (ou `contextId` en repli).
 * Routes admin globales (`/vehicles`, `/rentals`, `/invoices`). Retourne
 * `null` si pas de cible claire.
 */
export function notificationLink(n: Notification): unknown[] | null {
  const d = parseNotificationData(n.data);
  const ctx = n.contextId && n.contextId > 0 ? n.contextId : undefined;
  const vehicleId = d?.vehicleId ?? (isVehicleType(n.type) ? ctx : undefined);
  const rentalId = d?.rentalId ?? (isRentalType(n.type) ? ctx : undefined);
  const invoiceId = d?.invoiceId;

  switch (n.type) {
    case 'PROVISION_CONTRACT_SIGNING_REQUEST':
    case 'VEHICLE_STATUS_CHANGED':
    case 'VEHICLE_IN_REVIEW':
    case 'VEHICLE_REVIEWED_AVAILABLE':
    case 'VEHICLE_REVIEWED_REJECTED':
    case 'VEHICLE_DOCUMENT_EXPIRY':
    case 'PROVISION_WINDOW_UPDATED':
      return vehicleId ? ['/vehicles', vehicleId] : ['/vehicles'];
    case 'RENTAL':
    case 'RENTAL_ACCEPTED':
    case 'RENTAL_DENIED':
    case 'RENTAL_STARTED':
    case 'RENTAL_ENDED':
    case 'RENTAL_CANCELED':
    case 'RENTAL_CONTRACT_SIGNING_REQUEST':
    case 'RENTAL_REQUEST_EXPIRED':
    case 'RENTAL_REQUEST_REJECTED':
      return rentalId ? ['/rentals', rentalId] : ['/rentals'];
    case 'INVOICE_PAID':
      return invoiceId ? ['/invoices', invoiceId] : ['/invoices'];
    case 'PAYMENT_STATUS':
      return d?.transactionId ? ['/transactions', d.transactionId] : ['/transactions'];
    case 'NEW_CHAT':
      return rentalId ? ['/rentals', rentalId] : null;
    case 'OPERATOR_CHANGE':
      // Demande de changement d'opérateur (#36) → audit admin.
      return ['/change-requests'];
    case 'OPERATOR_APPROVED':
    case 'OPERATOR_CREDIT_LOW':
    case 'OPERATOR_CREDIT_EXHAUSTED':
    case 'OPERATOR_CREDIT_RESTORED':
      return ['/operators'];
    case 'KYC_REMINDER':
      return ['/kyc'];
    case 'OPERATOR_KYC_SUBMITTED':
      // Dossier KYC opérateur soumis (#66) → inbox de validation admin.
      return ['/operator-kyc'];
    case 'KYC_SUBMITTED':
      // Dossier permis client soumis → fiche client (écran alimenté par GET /{id}).
      return d?.customerId ? ['/customers', d.customerId] : ['/customers'];
    case 'DEBT_REMINDER':
    case 'OPERATOR_DEBT_REMINDER':
      // Rappels de dette auto (#51) → supervision des dettes.
      return ['/debts'];
    case 'WITHDRAWAL_TO_VALIDATE':
    case 'WITHDRAWAL_ADMIN_INFO':
      // Retraits BALANCE à valider / info seuil (#57) → inbox admin.
      return ['/withdrawals'];
    case 'INFO':
    default:
      return invoiceId ? ['/invoices', invoiceId] : null;
  }
}

function isVehicleType(t: NotificationType): boolean {
  return (
    t === 'VEHICLE_STATUS_CHANGED' ||
    t === 'VEHICLE_IN_REVIEW' ||
    t === 'PROVISION_CONTRACT_SIGNING_REQUEST' ||
    t === 'VEHICLE_DOCUMENT_EXPIRY' ||
    t === 'PROVISION_WINDOW_UPDATED'
  );
}

function isRentalType(t: NotificationType): boolean {
  return t.startsWith('RENTAL');
}
